// Vendor Dashboard — data layer.
//
// Everything the UI renders arrives from the host's bootstrap payload, one
// entry per module, each labelled with where it came from: a real provider
// ("live"), a demonstration fixture ("mock"), or nothing at all ("unavailable").
// No screen invents its own rows, so a section with no provider behind it shows
// an explicit gate rather than something that looks like a vendor's real draft.
//
// The static origin serves this bundle; the payload is fetched from the Django
// host at run time. There is no server-rendered HTML to read it out of, so a
// failed fetch is raised rather than defaulted: an empty dashboard must never be
// mistaken for a vendor who has no lots.

import { accessToken } from "./auth.js";

//: The host endpoint carrying the per-module bootstrap payload.
const BOOTSTRAP_URL = "/ui/bootstrap@1";

//: Fixture media and branding ship beside this bundle on the static origin.
const MOCK_MEDIA_BASE = "mock/";
const LOGO_URL = "img/inglis-logo-white.png";

// Realm selection is request state, never a bearer-like authority. It lives
// only in this bundle's memory and is attached to every host request.
let selectedRealm = new URLSearchParams(window.location.search).get("realm");

export function setSelectedRealm(realm) {
  selectedRealm = typeof realm === "string" && realm.trim() ? realm : null;
}

export function selectedRealmValue() {
  return selectedRealm;
}

/**
 * Raised when the host refuses the session rather than failing to answer.
 *
 * A rejected credential is not an outage: the page must offer sign-in again,
 * not tell the vendor to reload and hope.
 */
export class NotAuthenticated extends Error {}

/**
 * Fetch from the host with this session's token attached.
 *
 * Every host route the bundle calls is gated, so this is the only way the
 * bundle should reach one: a plain `fetch` arrives anonymous and a deployed
 * host answers it 401. The token is read per call rather than captured,
 * because `accessToken` silently renews an expired one.
 *
 * The local preview has no tenant to authenticate against; there the header is
 * simply absent and the host, in that environment alone, answers anyway.
 */
export async function authorizedFetch(url, options = {}) {
  const token = await accessToken();
  const headers = { Accept: "application/json", ...(options.headers || {}) };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (selectedRealm) {
    headers["X-SoftSystem-Realm"] = selectedRealm;
  }
  return fetch(url, { ...options, headers, credentials: "same-origin" });
}

// Profile shows preferences for every membership without changing the realm
// selected in the shell. The target must already be one of this signed-in
// user's bootstrap memberships; the host still authorises every request in
// that realm.
export async function authorizedRealmFetch(url, realm, options = {}) {
  const allowed = new Set((window.VD?.access?.realms || []).map(item => item.key));
  if (typeof realm !== "string" || !allowed.has(realm)) {
    throw new Error("realm_not_available");
  }
  const token = await accessToken();
  const headers = {
    Accept: "application/json",
    ...(options.headers || {}),
    "X-SoftSystem-Realm": realm,
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, credentials: "same-origin" });
}

/** Ask the host to send this visitor a fresh verification link. */
export async function requestVerificationEmail() {
  const response = await authorizedFetch("/ui/verification-email@1", { method: "POST" });
  if (response.status === 202) return { sent: true };
  if (response.ok) return await response.json();
  if (response.status === 429) {
    const body = await response.json().catch(() => ({}));
    return { sent: false, rateLimited: true, retryAfterSeconds: body.retryAfterSeconds || 60 };
  }
  if (response.status === 503) return { sent: false, unavailable: true };
  throw new Error(`verification email request failed: ${response.status}`);
}

/**
 * File this session's bug report or suggestion as a GitHub issue.
 *
 * The browser holds no GitHub credential and never calls GitHub: the host
 * files the issue with its own, and attaches the identity it verified rather
 * than anything claimed here. What the page sends is the description plus the
 * three facts only the window knows.
 */
export async function submitFeedback(description) {
  const response = await authorizedFetch("/ui/feedback@1", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      description,
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    }),
  });
  if (response.status === 201) return await response.json();
  if (response.status === 429) {
    const body = await response.json().catch(() => ({}));
    const error = new Error("feedback_rate_limited");
    error.retryAfterSeconds = body.retryAfterSeconds || 60;
    throw error;
  }
  if (response.status === 503) throw new Error("feedback_unavailable");
  if (response.status === 400 || response.status === 403) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || "invalid_request");
  }
  throw new Error("feedback_failed");
}

/** Fetch the bootstrap payload from the host, or raise a reportable error. */
export async function loadBootstrap() {
  const response = await authorizedFetch(BOOTSTRAP_URL);
  if (response.status === 401) {
    // The token was absent, rejected or expired mid-session. Which one is
    // deliberately not distinguishable, and it does not matter here: all three
    // are answered by signing in again.
    throw new NotAuthenticated("the host did not accept this session");
  }
  if (!response.ok) {
    if (response.status === 503) {
      const body = await response.json().catch(() => ({}));
      if (body.error === "identity_provider_unavailable") {
        throw new Error("Identity verification is temporarily unavailable. Wait 15 seconds, then retry.");
      }
    }
    throw new Error(`bootstrap request failed: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

/** Publish the payload as the window-level data layer the views read. */
export function applyBootstrap(boot) {

  var DATA_BY_MODULE = boot.data || {};
  var MODULES = boot.modules || {};

  function moduleInfo(name) {
    return MODULES[name] || { catalogue: "disabled", source: "unavailable", mockable: false };
  }
  function sourceOf(name) { return moduleInfo(name).source; }
  function isMocked(name) { return sourceOf(name) === "mock"; }
  function hasData(name) { return Object.prototype.hasOwnProperty.call(DATA_BY_MODULE, name); }
  function payload(name) { return DATA_BY_MODULE[name] || {}; }

  // ---- Clock ------------------------------------------------------------
  // With mock fixtures loaded the app runs on the demo clock the fixtures were
  // written around (Wed 14 Oct 2026, 07:45 — the day before 26R2R sells) so the
  // countdown, parade sessions and ring times all line up. Without them it is
  // simply the real time.
  var anchor = boot.demoClockAnchor ? new Date(boot.demoClockAnchor).getTime() : null;
  var loadedAt = Date.now();
  function now() {
    return anchor === null ? new Date() : new Date(anchor + (Date.now() - loadedAt));
  }

  // ---- Sale, vendor, lots ----------------------------------------------
  var mirror = payload("foundation.sale_lot_mirror");
  var registry = payload("foundation.vendor_registry");
  var stats = payload("vendor.stats").lots || {};

  var SALE = mirror.sale || {};
  var VENDOR = registry.vendor || {};
  var WEATHER = registry.weather || [];

  var EMPTY_STATS = { views: 0, plays: 0, xrays: 0, watchlist: 0, inspections: 0, parades: 0, trend: [] };

  var HORSES = (mirror.lots || []).map(function (lot) {
    var s = stats[lot.id] || EMPTY_STATS;
    var horse = {};
    Object.keys(lot).forEach(function (k) { horse[k] = lot[k]; });
    Object.keys(EMPTY_STATS).forEach(function (k) { horse[k] = s[k]; });
    horse.statsAvailable = !!stats[lot.id];
    horse.hot = horse.statsAvailable && (horse.watchlist > 34 || horse.views > 3200);
    return horse;
  });

  function ringTime(lot) {
    var start = SALE.sellingStartMinute != null ? SALE.sellingStartMinute : 660;
    var per = SALE.minutesPerLot != null ? SALE.minutesPerLot : 3.5;
    var mins = start + Math.round((lot - 1) * per);
    return pad(Math.floor(mins / 60)) + ":" + pad(mins % 60);
  }
  function pad(n) { return String(n).padStart(2, "0"); }

  // ---- Stable cameras ---------------------------------------------------
  var camera = payload("stable.camera_media");
  var BOX_IDS = camera.boxes || [];
  var CAMERAS = camera.cameras || {};
  var CAM_IMAGES = camera.images || {};
  var BOX_BLOCKS = camera.blocks || [];
  var SNAPSHOT_INTERVAL = camera.snapshotIntervalMinutes || 5;

  function camForBox(box) { return CAMERAS[String(box)] || "—"; }
  function camImgForBox(box) {
    var name = CAM_IMAGES[String(box)];
    return name ? MOCK_MEDIA_BASE + name : "";
  }

  // Prototype-only state. Deployed assignments use the governed box API;
  // old browser data must never become serving allocation authority.
  var demoAssignments = boot.environment === "local" || boot.environment === "ci" || boot.environment === "smoke";
  var ASSIGN_KEY = "vd-box-assign-" + (SALE.code || "sale");
  var DEFAULT_ASSIGN = camera.defaultAssignment || {};
  function loadAssign() {
    if (!demoAssignments) return {};
    try {
      var raw = window.localStorage.getItem(ASSIGN_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) { /* private mode, blocked storage — fall through to defaults */ }
    return Object.assign({}, DEFAULT_ASSIGN);
  }
  function saveAssign(map) {
    if (!demoAssignments) throw new Error("Use the server-backed box allocation controls.");
    try { window.localStorage.setItem(ASSIGN_KEY, JSON.stringify(map)); } catch (e) { /* not fatal */ }
  }

  // ---- Parade -----------------------------------------------------------
  var parade = payload("live.parade");
  var PARADE_AREAS = parade.areas || [];
  var PARADE_SCHEDULE = parade.schedule || [];
  var PARADE_SESSIONS = parade.sessions || [];
  var PARADE_STILL = parade.still ? MOCK_MEDIA_BASE + parade.still : "";

  function paradeSessionAt(d) {
    var m = d.getHours() * 60 + d.getMinutes();
    for (var i = 0; i < PARADE_SESSIONS.length; i++) {
      var s = PARADE_SESSIONS[i];
      if (m >= s.fromMinute && m < s.toMinute) return { name: s.name, ends: s.ends };
    }
    return null;
  }

  // ---- Inspections, TV, admin ------------------------------------------
  var inspections = payload("inspections");
  var tv = payload("tv");
  var admin = payload("vendor.admin");

  window.BUYERS = inspections.buyers || [];

  window.VD = {
    environment: boot.environment,
    catalogue: boot.catalogue,
    mocks: boot.mocks,
    modules: MODULES,
    moduleInfo: moduleInfo,
    source: sourceOf,
    isMocked: isMocked,
    hasData: hasData,
    sections: boot.sections || ["home"],
    home: boot.home || { enabled: false },
    reports: boot.reports || { enabled: false },
    boxAssignments: boot.boxAssignments || { enabled: false },
    cameraPlayback: boot.cameraPlayback || { enabled: false },
    lotContacts: boot.lotContacts || { enabled: false },
    // The host decides whether Inspections has a provider; `app.jsx` reads this
    // to choose between the governed panel and the session-only prototype. It
    // was missing until 18 September 2026, so a vendor holding the section and
    // all three read grants was shown the prototype's "No data source".
    inspections: boot.inspections || { enabled: false },
    repositoryStats: boot.repositoryStats || { configured: false, missing: [], pollSeconds: 300 },
    mockedModules: (boot.mocks && boot.mocks.modules) || [],
    logo: LOGO_URL,
    access: boot.access || {}
  };

  window.DATA = {
    SALE: SALE,
    VENDOR: VENDOR,
    WEATHER: WEATHER,
    HORSES: HORSES,
    BOX_IDS: BOX_IDS,
    BOX_BLOCKS: BOX_BLOCKS,
    SNAPSHOT_INTERVAL: SNAPSHOT_INTERVAL,
    camForBox: camForBox,
    camImgForBox: camImgForBox,
    loadAssign: loadAssign,
    saveAssign: saveAssign,
    PARADE_AREAS: PARADE_AREAS,
    PARADE_SCHEDULE: PARADE_SCHEDULE,
    PARADE_STILL: PARADE_STILL,
    paradeSessionAt: paradeSessionAt,
    INSPECTIONS: (inspections.recorded || []).slice(),
    CHANNELS: tv.channels || [],
    ROOMS: tv.rooms || [],
    SECTIONS: admin.sections || [],
    STAFF: admin.staff || [],
    ringTime: ringTime,
    now: now
  };
}
